import { EProjectPermission } from "@frontend-shared/types";
import { lazy } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";

import { useAppConfig } from "@/api/app-config/useAppConfig";
import { GlobalLoader } from "@/components/GlobalLoader";
import { PATHS } from "@/constants/paths";
import { useAgentsEnabled } from "@/hooks/use-agents-enabled";
import { useAnonymizationLabEnabled } from "@/hooks/use-anonymization-lab-enabled";
import { useAuth } from "@/hooks/use-auth";
import { useCallAssistantFeaturesEnabled } from "@/hooks/use-call-assistant-features-enabled";
import { useCallAssistantPermissions } from "@/hooks/use-call-assistant-permissions";
import { useCodeSessionsEnabled } from "@/hooks/use-code-sessions-enabled";
import { useCustomIntegrationEnabled } from "@/hooks/use-custom-integration-enabled";
import { useDocRecognitionEnabled } from "@/hooks/use-doc-recognition-enabled";
import { useIsBillingDisabled } from "@/hooks/use-is-billing-disabled";
import { usePermissionCheck } from "@/hooks/use-permission";
import { useSkillPermissions } from "@/hooks/use-skill-permissions";
import { useSkillsEnabled } from "@/hooks/use-skills-enabled";
import AcceptInvite from "@/pages/auth/AcceptInvite";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import OrganizationSso from "@/pages/auth/OrganizationSso";
import ResetPassword from "@/pages/auth/ResetPassword";
import Signin from "@/pages/auth/SignIn";
import Signup from "@/pages/auth/SignUp";
import Sso from "@/pages/auth/Sso";
import Token from "@/pages/auth/Token";
import AppLayout from "@/pages/layout";
import { ModelsRedirect } from "@/pages/models/ModelsRedirect";

import { NonProtectedRoute } from "./NonProtectedRoute";
import { ProtectedRoute } from "./ProtectedRoute";
import type { INonProtectedRouteConfig, IRouteConfig } from "./types";
import { createRouteElement } from "./utils";

const Chat = lazy(() => import("@/pages/chat"));
const Dashboards = lazy(() => import("@/pages/dashboards"));
const DashboardViewer = lazy(
  () => import("@/pages/dashboards/DashboardViewer"),
);
const CustomIntegrationBuilderPage = lazy(
  () => import("@/pages/integrations/CustomIntegrationBuilderPage"),
);
const Agents = lazy(() => import("@/pages/agents"));
const Code = lazy(() => import("@/pages/code"));
const CodeDetail = lazy(() => import("@/pages/code/CodeDetail"));
const AnonymizationLab = lazy(() => import("@/pages/anonymization-lab"));
const Assistants = lazy(() => import("@/pages/assistants"));
const Knowledge = lazy(() => import("@/pages/knowledge"));
const DocRecognitionQueue = lazy(() => import("@/pages/doc-recognition"));
const DocRecognitionReview = lazy(
  () => import("@/pages/doc-recognition/DocRecognitionReview"),
);
const GeneratedFiles = lazy(() => import("@/pages/generated-files"));
const Skills = lazy(() => import("@/pages/skills"));
const SkillDetail = lazy(() => import("@/pages/skills/SkillDetail"));
const NewSkill = lazy(() => import("@/pages/skills/NewSkill"));
const EditSkill = lazy(() => import("@/pages/skills/EditSkill"));
const SkillCatalog = lazy(() => import("@/pages/skills/catalog"));
const SkillCatalogEntry = lazy(
  () => import("@/pages/skills/catalog/SkillCatalogEntry"),
);
const Integrations = lazy(() => import("@/pages/integrations"));
const IntegrationDetails = lazy(() => import("@/pages/integration-details"));
const PromptLibrary = lazy(() => import("@/pages/prompt-library"));
const Analytics = lazy(() => import("@/pages/analytics"));
const Settings = lazy(() => import("@/pages/settings"));
const CallAssistant = lazy(() => import("@/pages/call-assistant"));
const MeetingDetails = lazy(() => import("@/pages/meetings-details"));
const Uploads = lazy(() => import("@/pages/uploads"));
const NewAssistant = lazy(() => import("@/pages/assistants/NewAssistant"));
const EditAssistant = lazy(() => import("@/pages/assistants/EditAssistant"));
const TrelloCallback = lazy(
  () => import("@/pages/integration-callback/TrelloCallback"),
);
const ChatFolders = lazy(() => import("@/pages/chat-folders"));
const Faq = lazy(() => import("@/pages/faq"));
const HtmlPreviewPage = lazy(() => import("@/pages/html-preview"));
const LicenseExpired = lazy(() => import("@/pages/license-expired"));
const NoProjects = lazy(() => import("@/pages/no-projects"));
const SelectPlan = lazy(() => import("@/pages/select-plan"));
const SharedChat = lazy(() => import("@/pages/shared-chat/SharedChat"));
const SharedMeeting = lazy(() => import("@/pages/shared-meeting"));
const SharedPage = lazy(() => import("@/pages/shared-page"));
const SharedPublicChat = lazy(() => import("@/pages/shared-public-chat"));

export const AppRoutes = (): JSX.Element => {
  const { isPersonalProject, user } = useAuth();
  const { isBillingDisabled } = useIsBillingDisabled();
  const { isLoading: isAppConfigLoading } = useAppConfig();
  const isCallAssistantFeaturesEnabled = useCallAssistantFeaturesEnabled();
  const isCallAssistantFeaturesResolved = !!user && !isAppConfigLoading;
  const isCallAssistantFeaturesHidden =
    isCallAssistantFeaturesResolved && !isCallAssistantFeaturesEnabled;
  // NOTE: The Notetaker follows the ACTIVE project's call_assistant:read.
  // In a personal project usePermissionCheck sees the "*" wildcard and returns
  // true, so it stays available there; in an org project without the permission
  // the tab/routes disappear (the user switches to a project that grants it).
  const { canRead: hasCallAssistantReadPermission } =
    useCallAssistantPermissions();
  const isCallAssistantHidden =
    isCallAssistantFeaturesHidden || !hasCallAssistantReadPermission;
  const [
    hasPrivateAssistantReadPermission,
    hasPrivateAssistantWritePermission,
    hasIntegrationReadPermission,
    hasPromptLibraryReadPermission,
    hasAllMembersAnalyticsReadPermission,
    hasAnalyticsReadPermission,
    hasProbeReadPermission,
    hasKnowledgeReadPermission,
    hasAutomationReadPermission,
    hasPublicAssistantReadPermission,
    hasPublicAssistantWritePermission,
    hasCustomIntegrationWritePermission,
    hasCustomIntegrationReadPermission,
    hasDocRecognitionReadPermission,
    hasDashboardReadPermission,
  ] = usePermissionCheck([
    EProjectPermission.PRIVATE_ASSISTANT_READ,
    EProjectPermission.PRIVATE_ASSISTANT_WRITE,
    EProjectPermission.INTEGRATION_READ,
    EProjectPermission.PROMPT_LIBRARY_READ,
    EProjectPermission.ALL_MEMBERS_ANALYTICS_READ,
    EProjectPermission.ANALYTICS_READ,
    EProjectPermission.PROBE_READ,
    EProjectPermission.KNOWLEDGE_READ,
    EProjectPermission.AUTOMATION_READ,
    EProjectPermission.PUBLIC_ASSISTANT_READ,
    EProjectPermission.PUBLIC_ASSISTANT_WRITE,
    EProjectPermission.CUSTOM_INTEGRATION_WRITE,
    EProjectPermission.CUSTOM_INTEGRATION_READ,
    EProjectPermission.DOC_RECOGNITION_READ,
    EProjectPermission.DASHBOARD_READ,
  ]);
  // NOTE: Either private or public assistant read/write opens the relevant routes.
  // The server enforces the per-type permission inside each endpoint.
  const hasAssistantReadPermission =
    hasPrivateAssistantReadPermission || hasPublicAssistantReadPermission;
  const hasAssistantWritePermission =
    hasPrivateAssistantWritePermission || hasPublicAssistantWritePermission;

  const isAgentsEnabled = useAgentsEnabled();
  const isAnonymizationLabEnabled = useAnonymizationLabEnabled();
  const isCodeSessionsEnabled = useCodeSessionsEnabled();
  const isCustomIntegrationEnabled = useCustomIntegrationEnabled();
  const isDocRecognitionEnabled = useDocRecognitionEnabled();
  // NOTE: Mirror the call-assistant pattern — do not hide custom integration
  // routes until /app-config has resolved. Without this guard the hook returns
  // false on the initial render and immediately redirects deep-linkers to chat
  // even when the flag is actually enabled on the server.
  const isCustomIntegrationResolved = !!user && !isAppConfigLoading;
  const isCustomIntegrationHidden =
    isCustomIntegrationResolved && !isCustomIntegrationEnabled;
  // NOTE: Same app-config-loading race guard — don't hide the lab route until
  // /app-config has resolved, otherwise a first-render false redirects a
  // legitimate deep-linker to chat even when the flag is enabled on the server.
  const isAnonymizationLabResolved = !!user && !isAppConfigLoading;
  const isAnonymizationLabHidden =
    isAnonymizationLabResolved && !isAnonymizationLabEnabled;
  // NOTE: NSAI-3719 — same app-config-loading race guard for the /code routes:
  // while /app-config is in flight the flag hook reads false, and hiding on
  // that would bounce a legitimate deep-linker to chat even with the flag
  // enabled on the server.
  const isCodeSessionsResolved = !!user && !isAppConfigLoading;
  const isCodeSessionsHidden = isCodeSessionsResolved && !isCodeSessionsEnabled;
  // NOTE: Same guard for NSAI-4317 — while /app-config is in flight the flag hook
  // reads false, and hiding the route on that would send a deep-linker to chat with
  // a replace() they cannot come back from. Treat "not yet known" as not hidden and
  // let the flag decide once it has resolved; the permission check is independent
  // of /app-config, so it applies immediately (matching every other route here).
  const isDocRecognitionResolved = !!user && !isAppConfigLoading;
  const isDocRecognitionHidden =
    (isDocRecognitionResolved && !isDocRecognitionEnabled) ||
    !hasDocRecognitionReadPermission;

  // NOTE: NSAI-3306 — same app-config-loading race guard as doc recognition.
  // While /app-config is in flight useSkillsEnabled reads false, and hiding the
  // route on that would replace() a deep-linker into chat with no way back.
  // "Not yet known" is therefore treated as not hidden; the permission check is
  // independent of /app-config and applies immediately.
  const isSkillsEnabled = useSkillsEnabled();
  const {
    canReadAny: hasAnySkillReadPermission,
    canCreateAny: canCreateAnySkill,
    canReadOrgCatalog: hasOrganizationSkillReadPermission,
  } = useSkillPermissions();
  const isSkillsResolved = !!user && !isAppConfigLoading;
  const isSkillsHidden =
    (isSkillsResolved && !isSkillsEnabled) || !hasAnySkillReadPermission;
  // NOTE: NSAI-3311 — the Organization Catalog needs the Skills flag AND
  // organization_skill:read, which is a permission of its own: browsing what the
  // organization published is not implied by managing the project's own skills.
  // Same app-config-loading race guard as above — "not yet known" is not hidden.
  const isSkillCatalogHidden =
    (isSkillsResolved && !isSkillsEnabled) ||
    !hasOrganizationSkillReadPermission;

  const nonProtectedRoutes: INonProtectedRouteConfig[] = [
    { path: PATHS.AUTH.SIGN_IN, element: <Signin /> },
    { path: PATHS.AUTH.SIGN_UP, element: <Signup /> },
    { path: PATHS.AUTH.TOKEN, element: <Token /> },
    { path: PATHS.AUTH.SSO, element: <Sso /> },
    { path: PATHS.AUTH.SSO_ORGANIZATION, element: <OrganizationSso /> },
    { path: PATHS.AUTH.FORGOT_PASSWORD, element: <ForgotPassword /> },
    { path: PATHS.AUTH.RESET_PASSWORD, element: <ResetPassword /> },
    { path: PATHS.AUTH.ACCEPT_INVITE, element: <AcceptInvite /> },
  ];

  const protectedRoutes: IRouteConfig[] = [
    { path: PATHS.CHAT, element: <Chat /> },
    { path: `${PATHS.CHAT}/:chatId`, element: <Chat /> },
    {
      // NOTE: NSAI-5657 — the ROUTE is gated, not just the nav entry, so a deep
      // link to a dashboard cannot open a surface every endpoint would refuse.
      path: PATHS.DASHBOARDS,
      element: <Dashboards />,
      isHidden: !hasDashboardReadPermission,
    },
    {
      path: PATHS.DASHBOARD_DETAIL,
      element: <DashboardViewer />,
      isHidden: !hasDashboardReadPermission,
    },
    {
      // NOTE: NSAI-6657 — the Models page is a Profile settings tab now; the
      // tab applies the model:read gate, so the redirect itself is open.
      path: PATHS.MODELS,
      element: <ModelsRedirect />,
    },
    {
      path: PATHS.ASSISTANTS,
      element: <Assistants />,
      isHidden: !hasAssistantReadPermission,
    },
    {
      path: PATHS.AGENTS,
      element: <Agents />,
      isHidden: !isAgentsEnabled || !hasAutomationReadPermission,
    },
    {
      path: PATHS.ANONYMIZATION_LAB,
      element: <AnonymizationLab />,
      isHidden: isAnonymizationLabHidden,
    },
    {
      path: PATHS.CODE,
      element: <Code />,
      isHidden: isCodeSessionsHidden,
    },
    {
      path: PATHS.CODE_DETAIL,
      element: <CodeDetail />,
      isHidden: isCodeSessionsHidden,
    },
    {
      path: PATHS.NEW_ASSISTANT,
      element: <NewAssistant />,
      isHidden: !hasAssistantWritePermission,
    },
    {
      path: PATHS.EDIT_ASSISTANT,
      element: <EditAssistant />,
      isHidden: !hasAssistantWritePermission,
    },
    {
      path: PATHS.KNOWLEDGE,
      element: <Knowledge />,
      isHidden: !hasKnowledgeReadPermission,
    },
    {
      // NOTE: NSAI-4317 — hidden unless the backend flag is on AND the user holds
      // doc_recognition:read. The backend enforces both independently, so this only
      // keeps the route out of the SPA when it could never succeed.
      path: PATHS.DOC_RECOGNITION,
      element: <DocRecognitionQueue />,
      isHidden: isDocRecognitionHidden,
    },
    {
      path: PATHS.DOC_RECOGNITION_DOCUMENT,
      element: <DocRecognitionReview />,
      isHidden: isDocRecognitionHidden,
    },
    {
      // NOTE: Not gated on KNOWLEDGE_READ — AI Generated Files only shows the
      // logged-in user's own files (scoped server-side by userId).
      path: PATHS.AI_GENERATED_FILES,
      element: <GeneratedFiles />,
    },
    {
      // NOTE: NSAI-3306 — the ROUTE is gated, not just the nav entry, so a deep
      // link cannot reach a surface the backend would refuse anyway.
      path: PATHS.SKILLS,
      element: <Skills />,
      isHidden: isSkillsHidden,
    },
    {
      // NOTE: Both catalog paths win over "/skills/:skillUuid" for the same reason
      // "/skills/new" does — a static segment outranks a dynamic one.
      path: PATHS.SKILL_CATALOG,
      element: <SkillCatalog />,
      isHidden: isSkillCatalogHidden,
    },
    {
      path: PATHS.SKILL_CATALOG_ENTRY,
      element: <SkillCatalogEntry />,
      isHidden: isSkillCatalogHidden,
    },
    {
      // NOTE: "/skills/new" wins over "/skills/:skillUuid" because React Router
      // ranks a static segment above a dynamic one, independent of order here.
      path: PATHS.SKILL_NEW,
      element: <NewSkill />,
      isHidden: isSkillsHidden || !canCreateAnySkill,
    },
    {
      path: PATHS.SKILL_EDIT,
      element: <EditSkill />,
      isHidden: isSkillsHidden || !canCreateAnySkill,
    },
    {
      path: PATHS.SKILL_DETAIL,
      element: <SkillDetail />,
      isHidden: isSkillsHidden,
    },
    {
      path: PATHS.INTEGRATIONS,
      element: <Integrations />,
      isHidden:
        !hasIntegrationReadPermission &&
        !(hasCustomIntegrationReadPermission && !isCustomIntegrationHidden),
    },
    {
      path: PATHS.CUSTOM_INTEGRATION_NEW,
      element: <CustomIntegrationBuilderPage />,
      isHidden:
        !hasCustomIntegrationWritePermission || isCustomIntegrationHidden,
    },
    {
      path: PATHS.CUSTOM_INTEGRATION_EDIT,
      element: <CustomIntegrationBuilderPage />,
      isHidden:
        !hasCustomIntegrationWritePermission || isCustomIntegrationHidden,
    },
    {
      path: PATHS.INTEGRATION_DETAILS,
      element: <IntegrationDetails />,
      isHidden: !hasIntegrationReadPermission,
    },
    {
      path: PATHS.PROMPT_LIBRARY,
      element: <PromptLibrary />,
      isHidden: !hasPromptLibraryReadPermission,
    },
    {
      path: PATHS.ANALYTICS,
      element: <Analytics />,
      isHidden:
        isPersonalProject ||
        (!hasAllMembersAnalyticsReadPermission &&
          !hasAnalyticsReadPermission &&
          !hasProbeReadPermission),
    },
    {
      path: PATHS.CALL_ASSISTANT,
      element: <CallAssistant />,
      isHidden: isCallAssistantHidden,
    },
    {
      path: PATHS.MEETING_DETAILS,
      element: <MeetingDetails />,
      isHidden: isCallAssistantHidden,
    },
    {
      path: PATHS.UPLOADS,
      element: <Uploads />,
      isHidden: isCallAssistantHidden,
    },
    { path: PATHS.CHAT_FOLDERS, element: <ChatFolders /> },
    { path: PATHS.FAQ, element: <Faq /> },
    { path: PATHS.SETTINGS, element: <Settings /> },
    { path: PATHS.SHARED_CHAT, element: <SharedChat /> },
  ];

  return (
    <GlobalLoader>
      <Routes>
        <Route index element={<Navigate to={PATHS.CHAT} replace />} />
        <Route
          element={
            <NonProtectedRoute>
              <Outlet />
            </NonProtectedRoute>
          }
        >
          {nonProtectedRoutes.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={createRouteElement(route.element)}
            />
          ))}
        </Route>

        <Route
          element={createRouteElement(
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>,
          )}
        >
          {protectedRoutes.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={
                route.isHidden ? (
                  <Navigate to={PATHS.CHAT} replace />
                ) : (
                  createRouteElement(route.element)
                )
              }
            />
          ))}
        </Route>

        {/* NOTE: NSAI-5656 — a generated HTML page in its own window. Signed-in
            only, but outside AppLayout so the page gets the whole viewport. */}
        <Route
          path={PATHS.HTML_PREVIEW}
          element={createRouteElement(
            <ProtectedRoute>
              <HtmlPreviewPage />
            </ProtectedRoute>,
          )}
        />

        {/* NOTE: License expired — standalone (no ProtectedRoute) to avoid
            useGetUser() → 403 → redirect → useGetUser() infinite loop. */}
        <Route
          path={PATHS.LICENSE_EXPIRED}
          element={createRouteElement(<LicenseExpired />)}
        />

        {/* NOTE: Trello OAuth callback — must be unguarded so the #token=... fragment
            is never lost due to a ProtectedRoute redirect during auth hydration. */}
        <Route
          path={PATHS.TRELLO_CALLBACK}
          element={createRouteElement(<TrelloCallback />)}
        />

        {/* NOTE: Public, password-gated meeting viewer (NSAI-3853). Standalone —
            no ProtectedRoute/NonProtectedRoute — so both signed-out visitors and
            signed-in users can open a shared link. */}
        <Route
          path={PATHS.SHARED_MEETING}
          element={createRouteElement(<SharedMeeting />)}
        />

        {/* NOTE: NSAI-5827 — public, password-gated viewer for a generated
            page. Standalone, like the meeting viewer above: both a signed-out
            visitor and a signed-in user must be able to open a shared link. */}
        <Route
          path={PATHS.SHARED_PAGE}
          element={createRouteElement(<SharedPage />)}
        />

        {/* NOTE: Public, password-gated chat viewer (NSAI-5823). Standalone for
            the same reason as the two viewers above — no ProtectedRoute — so a
            recipient without an NSAI account can open the link at all. */}
        <Route
          path={PATHS.SHARED_PUBLIC_CHAT}
          element={createRouteElement(<SharedPublicChat />)}
        />

        {/* Select Plan Route */}
        <Route
          path={PATHS.SELECT_PLAN}
          element={createRouteElement(
            isBillingDisabled ? (
              <Navigate to={PATHS.CHAT} replace />
            ) : (
              <ProtectedRoute>
                <SelectPlan />
              </ProtectedRoute>
            ),
          )}
        />

        {/* No Projects Route */}
        <Route
          path={PATHS.NO_PROJECTS}
          element={createRouteElement(
            <ProtectedRoute>
              <NoProjects />
            </ProtectedRoute>,
          )}
        />

        {/* 404 Route */}
        <Route path="*" element={<Navigate to={PATHS.CHAT} replace />} />
      </Routes>
    </GlobalLoader>
  );
};
